import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function PrivacyPage() {
  const lastUpdated = '2026-06-15';

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="container-page">
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-5 tracking-wide">
                LEGAL
              </span>
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                Privacy Policy
              </h1>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                How Freshhlock collects, uses, and protects your information when you use our platform.
              </p>
              <p className="text-xs text-foreground-500 mt-4">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="bg-background-50 py-12 md:py-16">
          <div className="container-page">
            <div className="max-w-3xl mx-auto space-y-10 text-foreground-700 leading-relaxed text-sm md:text-base">

              {/* Section 1 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">1. Introduction</h2>
                <p>
                  Freshhlock (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the Freshhlock platform (&ldquo;Platform&rdquo;), which connects warehouse operators, transport providers, wholesale suppliers, retailers, and processing facilities across India&apos;s frozen food cold-chain. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data when you access or use our Platform.
                </p>
                <p className="mt-3">
                  By using the Platform, you consent to the practices described in this policy. If you do not agree, please discontinue use of the Platform immediately.
                </p>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">2. Information We Collect</h2>
                <p className="mb-3">We collect the following categories of information:</p>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded bg-accent-100 text-accent-600 mt-0.5">
                      <i className="ri-user-line text-xs"></i>
                    </div>
                    <div>
                      <strong className="text-foreground-900">Personal Identification Information:</strong>
                      <span> Name, email address, phone number, GSTIN, PAN, business name, and physical address provided during account registration or KYC verification.</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded bg-accent-100 text-accent-600 mt-0.5">
                      <i className="ri-smartphone-line text-xs"></i>
                    </div>
                    <div>
                      <strong className="text-foreground-900">Device &amp; Usage Data:</strong>
                      <span> IP address, browser type, operating system, device identifiers, page views, click patterns, session duration, and referring URLs collected automatically via cookies and similar technologies.</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded bg-accent-100 text-accent-600 mt-0.5">
                      <i className="ri-truck-line text-xs"></i>
                    </div>
                    <div>
                      <strong className="text-foreground-900">Logistics &amp; Transaction Data:</strong>
                      <span> Order details, shipment tracking information, warehouse storage records, temperature logs, invoice data, payment history, and platform transaction metadata.</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded bg-accent-100 text-accent-600 mt-0.5">
                      <i className="ri-customer-service-2-line text-xs"></i>
                    </div>
                    <div>
                      <strong className="text-foreground-900">Communication Data:</strong>
                      <span> Messages sent through our in-app chat, support tickets, feedback submissions, survey responses, and call recordings (with prior consent).</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">3. How We Use Your Information</h2>
                <p className="mb-3">We use collected information for the following purposes:</p>
                <ul className="space-y-2 list-disc pl-5">
                  <li>To create, maintain, and verify your platform account and KYC compliance.</li>
                  <li>To facilitate cold-chain matching — connecting warehouse partners with storage seekers, transport partners with shippers, and suppliers with buyers.</li>
                  <li>To process transactions, generate invoices, and maintain financial records as required under Indian law.</li>
                  <li>To improve platform performance, personalise user experience, and develop new features.</li>
                  <li>To send transactional notifications (booking confirmations, dispatch alerts, temperature breach warnings) and service-related communications.</li>
                  <li>To detect, prevent, and address fraud, unauthorised access, and violations of our Terms of Service.</li>
                  <li>To comply with applicable legal obligations, court orders, and regulatory requirements.</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">4. Data Sharing &amp; Disclosure</h2>
                <p>
                  We do not sell your personal data. We may share information in the following limited circumstances:
                </p>
                <div className="mt-3 space-y-2.5">
                  <p><strong className="text-foreground-900">With Platform Participants:</strong> When you book a warehouse slot, your business name and contact details are shared with the warehouse operator to fulfil the booking. Similarly, transport partners receive shipment details necessary for delivery.</p>
                  <p><strong className="text-foreground-900">With Service Providers:</strong> We engage trusted third parties for payment processing, cloud hosting, SMS/email delivery, and analytics. These providers are bound by data processing agreements and may only use data as instructed by us.</p>
                  <p><strong className="text-foreground-900">Legal Compliance:</strong> We may disclose data to government authorities, law enforcement, or regulators when required by applicable law, court order, or to protect our legal rights.</p>
                  <p><strong className="text-foreground-900">Business Transfers:</strong> In the event of a merger, acquisition, or asset sale, user data may be transferred as part of the transaction, subject to this Privacy Policy continuing to apply.</p>
                </div>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">5. Data Retention</h2>
                <p>
                  We retain your personal data only as long as necessary to fulfil the purposes outlined in this policy. Account data is retained while your account remains active. Transaction and logistics records are retained for a minimum of eight (8) years as required under Indian tax and commercial laws (including the Companies Act, 2013 and GST regulations). Upon account deletion, we anonymise or delete personal data within 90 days, except where retention is legally mandated.
                </p>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">6. Your Rights</h2>
                <p className="mb-3">Under applicable Indian data protection laws, you have the following rights:</p>
                <ul className="space-y-2 list-disc pl-5">
                  <li><strong className="text-foreground-900">Access &amp; Rectification:</strong> You may request a copy of the personal data we hold about you and ask us to correct any inaccuracies.</li>
                  <li><strong className="text-foreground-900">Data Portability:</strong> You may request your data in a structured, machine-readable format.</li>
                  <li><strong className="text-foreground-900">Withdraw Consent:</strong> You may withdraw consent for non-essential data processing at any time, without affecting the lawfulness of processing prior to withdrawal.</li>
                  <li><strong className="text-foreground-900">Grievance Redressal:</strong> You may contact our Grievance Officer (details below) with any concerns about data handling.</li>
                </ul>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">7. Data Security</h2>
                <p>
                  We implement industry-standard technical and organisational measures — including encryption at rest and in transit (TLS 1.3), role-based access controls, regular penetration testing, and audit logging — to protect your data against unauthorised access, alteration, disclosure, or destruction. However, no method of electronic storage or transmission is 100% secure. You are responsible for maintaining the confidentiality of your account credentials.
                </p>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">8. Children&apos;s Privacy</h2>
                <p>
                  Our Platform is not intended for individuals under the age of 18. We do not knowingly collect personal data from children. If we become aware that a child has provided us with personal data, we will take steps to delete such information promptly.
                </p>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">9. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. Material changes will be communicated via email and/or a prominent notice on the Platform at least 15 days before the changes take effect. Continued use of the Platform after the effective date constitutes acceptance of the revised policy.
                </p>
              </div>

              {/* Section 10 */}
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-3">10. Grievance Officer</h2>
                <p>
                  For any questions, concerns, or complaints regarding this Privacy Policy or our data practices, please contact our designated Grievance Officer:
                </p>
                <div className="mt-3 p-5 rounded-lg bg-card-50 border border-card-200 space-y-1.5 text-sm shadow-sm">
                  <p><strong className="text-foreground-900">Name:</strong> Data Protection Officer</p>
                  <p><strong className="text-foreground-900">Email:</strong> privacy@freshhlock.in</p>
                  <p><strong className="text-foreground-900">Address:</strong> Freshhlock Technologies Pvt. Ltd., 4th Floor, DLF Cyber City, Gurugram, Haryana — 122002, India</p>
                  <p><strong className="text-foreground-900">Response Time:</strong> We aim to acknowledge all grievances within 48 hours and resolve them within 30 days.</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}